import React, { useState, useMemo, useEffect } from 'react';
import {
  UsersRound,
  LayoutDashboard,
  TrendingUp,
  Palmtree,
  UserRound,
  Building2,
  Clock,
  CalendarDays,
  Brain,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentMonthKeySingapore } from '../utils/dateUtils';
import { useTabPersistence } from '../hooks/usePersistence';
import {
  useROTeamOverview,
  useROTeamTrends,
  useROTeamLeaveInsights,
  useROTeamIndividual,
  useROTeamUpcomingLeave,
} from '../hooks/useROTeamData';
import {
  useHODRoBreakdown,
  useHODTeamOverview,
  useHODTeamTrends,
  useHODTeamLeaveInsights,
  useHODTeamIndividual,
} from '../hooks/useHODTeamData';
import TeamOverview from '../components/ROTeam/TeamOverview';
import TeamTrends from '../components/ROTeam/TeamTrends';
import LeaveInsights from '../components/ROTeam/LeaveInsights';
import IndividualDetail from '../components/ROTeam/IndividualDetail';
import DepartmentOverview from '../components/ROTeam/DepartmentOverview';
import TeamOvertimePanel from '../components/ROTeam/TeamOvertimePanel';
import TeamRestDayPanel from '../components/ROTeam/TeamRestDayPanel';
import TeamBigFiveTab from '../components/ROTeam/TeamBigFiveTab';
import { useMyTeamOvertimeEligibility } from '../hooks/useMyTeamOvertimeData';

// ---------------------------------------------------------------------------
// Tab config — uses canonical tab-btn CSS classes (employee theme)
// ---------------------------------------------------------------------------

const RO_TABS = [
  { key: 'overview',   label: 'Overview',       icon: LayoutDashboard },
  { key: 'trends',     label: 'Trends',         icon: TrendingUp },
  { key: 'leave',      label: 'Leave Insights', icon: Palmtree },
  { key: 'individual', label: 'Individual',      icon: UserRound },
];

// Career-conversation surface — visible to ROs and HODs/Deputies, not to
// Trade Leaders / portal Managers who only have compliance scope.
const BIG_FIVE_TAB = { key: 'big-five', label: 'Big Five', icon: Brain };

const DEPARTMENT_TAB = { key: 'department', label: 'Department', icon: Building2 };

// Compliance monitoring tabs — manager-scoped twins of the HR admin OT /
// rest-day dashboards. Available to BOTH ROs and HODs (every manager needs
// visibility of their own people), so they are NOT gated behind the RO-only
// check that hides the overview/trends/leave/individual tabs for HOD-only
// users. Each panel resolves its own scope server-side.
const COMPLIANCE_TABS = [
  { key: 'overtime', label: 'Overtime', icon: Clock },
  { key: 'restday', label: 'Rest Day', icon: CalendarDays },
];

// Tab keys that stay visible even when the user is not an RO (HOD-only):
// Big Five + Department + compliance tabs.
const NON_RO_VISIBLE_KEYS = [
  BIG_FIVE_TAB.key,
  'department',
  ...COMPLIANCE_TABS.map((t) => t.key),
];

// Static list of ALL possible tab keys — used for URL persistence validation.
// Must never change between renders; tab visibility is handled separately in render.
const ALL_TAB_KEYS = [
  ...RO_TABS.map((t) => t.key),
  BIG_FIVE_TAB.key,
  ...COMPLIANCE_TABS.map((t) => t.key),
  'department',
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ROTeamDashboard() {
  const { user } = useAuth();

  // Tab persistence (must come before hooks that depend on activeTab)
  const [activeTab, setActiveTab] = useTabPersistence('ro-team-dashboard', 'overview', ALL_TAB_KEYS);

  // HOD probe — lightweight, fires for everyone (403 = not HOD, silent)
  const hodBreakdownQuery = useHODRoBreakdown();

  const isHOD =
    !hodBreakdownQuery.isLoading &&
    !hodBreakdownQuery.error &&
    hodBreakdownQuery.data != null;

  // Defer expensive HOD endpoints until confirmed HOD.
  // Overview fires once HOD is confirmed (needed for Individual tab member list).
  // Trends + leave only fire when the Department tab is active.
  const hodOverviewQuery = useHODTeamOverview(undefined, { enabled: isHOD });
  const deptTabActive = isHOD && activeTab === 'department';
  const hodTrendsQuery = useHODTeamTrends(8, { enabled: deptTabActive });
  const hodLeaveQuery = useHODTeamLeaveInsights(undefined, { enabled: deptTabActive });

  // Overtime tab eligibility — 72h monitoring is workmen-only. A manager
  // can have a team (e.g. only staff-class reports) yet zero overtime-
  // eligible workmen, in which case the OT tab would only ever show an
  // empty state, so we hide it entirely (the S0133 case). Cheap count
  // query. Fail-open on error so a transient failure never hides the tab
  // for a genuinely-eligible manager. Hidden until resolved to avoid a
  // show-then-vanish flicker.
  const overtimeEligibilityQuery = useMyTeamOvertimeEligibility();
  const overtimeEligible = overtimeEligibilityQuery.isError
    ? true
    : overtimeEligibilityQuery.data?.has_overtime_team === true;

  // Visible tabs for rendering — Department only shown for confirmed HODs;
  // Overtime only when the manager has overtime-eligible workmen.
  // Big Five sits after Individual (career prep, not attendance).
  const visibleTabs = useMemo(() => {
    const tabs = [...RO_TABS, BIG_FIVE_TAB, ...COMPLIANCE_TABS];
    if (isHOD) tabs.push(DEPARTMENT_TAB);
    return tabs.filter((t) => t.key !== 'overtime' || overtimeEligible);
  }, [isHOD, overtimeEligible]);

  // Once HOD data settles: if the user landed on 'department' but is not an HOD,
  // reset to 'overview' (e.g., stale localStorage or shared URL from an HOD).
  useEffect(() => {
    if (activeTab === 'department' && !hodBreakdownQuery.isLoading && !isHOD) {
      setActiveTab('overview');
    }
  }, [activeTab, hodBreakdownQuery.isLoading, isHOD, setActiveTab]);

  // Individual tab state
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => getCurrentMonthKeySingapore());

  // Overview-tab scope toggle (HODs only).
  //
  // ROs see only their direct reports. HODs straddle two natural
  // mental models:
  //   - "How is MY team doing today?" (direct reports only — the same
  //     people they 1:1 with), AND
  //   - "How is my whole department doing today?" (all employees in
  //     the departments they head, including indirect reports).
  //
  // The toggle lets them switch in-place on the Overview tab without
  // jumping to the Department tab (which has a richer breakdown but
  // is more cognitively expensive). Defaults to 'direct-reports' so
  // an HOD who is ALSO an RO sees the familiar narrower view first.
  // Stays at 'direct-reports' for non-HODs (toggle is hidden).
  const [overviewScope, setOverviewScope] = useState('direct-reports');
  useEffect(() => {
    if (!isHOD && overviewScope !== 'direct-reports') {
      setOverviewScope('direct-reports');
    }
  }, [isHOD, overviewScope]);

  // Data hooks (RO scope)
  const overviewQuery = useROTeamOverview();
  const trendsQuery = useROTeamTrends(8);
  const leaveQuery = useROTeamLeaveInsights();
  const upcomingLeaveQuery = useROTeamUpcomingLeave();

  // Individual detail — HOD endpoint covers all department staff (including
  // indirect reports), RO endpoint only covers direct reports. Use the
  // appropriate one based on the user's role.
  const roIndividualQuery = useROTeamIndividual(
    !isHOD ? selectedStaffId : null,
    selectedMonth,
  );
  const hodIndividualQuery = useHODTeamIndividual(
    isHOD ? selectedStaffId : null,
    selectedMonth,
  );
  const individualQuery = isHOD ? hodIndividualQuery : roIndividualQuery;

  // Team member list for the Individual selector — merge RO + HOD if applicable
  const teamMembers = useMemo(() => {
    const roMembers = (overviewQuery.data?.members ?? []).map((m) => ({
      staff_id: m.staff_id,
      full_name: m.full_name,
      classification: m.classification,
    }));
    if (!isHOD) return roMembers;

    const hodMembers = (hodOverviewQuery.data?.members ?? []).map((m) => ({
      staff_id: m.staff_id,
      full_name: m.full_name,
      classification: m.classification,
      department_name: m.department_name || null,
    }));

    // Build a lookup so RO-scope members (who lack department_name) can be
    // enriched with the value from the HOD-scope response when available.
    const hodByStaffId = new Map(hodMembers.map((m) => [m.staff_id, m]));

    const roMembersEnriched = roMembers.map((m) => ({
      ...m,
      department_name: hodByStaffId.get(m.staff_id)?.department_name || null,
    }));

    const seen = new Set(roMembersEnriched.map((m) => m.staff_id));
    const merged = [...roMembersEnriched];
    for (const m of hodMembers) {
      if (!seen.has(m.staff_id)) {
        merged.push(m);
        seen.add(m.staff_id);
      }
    }
    return merged.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [overviewQuery.data, hodOverviewQuery.data, isHOD]);

  // 403 guard — not an RO (and not an HOD)
  const isRO403 =
    overviewQuery.error?.response?.status === 403 ||
    (!overviewQuery.isLoading && !overviewQuery.data && !user?.is_reporting_officer);

  // ROs and HODs/Deputies get Big Five; Trade Leaders / Managers alone do not.
  const canViewBigFive = !isRO403 || isHOD;

  // Trade Leaders manage everyone in the trade(s) they lead — the
  // inverse of the L1 routing in overtime_notifications/recipients.py
  // ("L1 is reporting_officer + trade_category.trade_leader"). They are
  // often NOT the RO or HOD of their workmen (e.g. Teams, where the
  // Trade Leader is the L1 supervisor and the RO is skipped), so they
  // must reach the compliance tabs even when isRO403 and not isHOD.
  // ``is_trade_leader`` is server-computed in StaffUserViewSet.me via
  // NightworkService.is_trade_leader (TradeCategory.trade_leader=user).
  const isTradeLeader = !!user?.is_trade_leader;

  // Manager portal-group members get visibility of their OWN department
  // (TeamScopeService grants ``department = user.department``), so they
  // must also reach the compliance tabs even when not an RO/HOD.
  const isManager = !!user?.is_manager;

  // Lead Trade Leaders sit above the Trade Leaders but below the Deputy
  // HOD/HOD; TeamScopeService grants them department-wide visibility
  // (Department.lead_trade_leader = user), so they reach the compliance
  // tabs even when not an RO/HOD. Server-computed in StaffUserViewSet.me.
  const isLeadTradeLeader = !!user?.is_lead_trade_leader;

  // The empty state is only for users who manage NOBODY by any
  // relationship (not an RO, HOD, Trade Leader, Manager, or Lead Trade Leader).
  const showEmptyState =
    isRO403 && !isHOD && !isTradeLeader && !isManager && !isLeadTradeLeader;

  // HOD-only managers (not ROs) can't see the RO-scoped tabs
  // (overview/trends/leave/individual). If they land on one — e.g. the
  // default 'overview', or a stale URL — bump them to the first available
  // compliance tab so they never face a blank panel. Prefer Overtime when
  // they have eligible workmen, otherwise Rest Day (always visible).
  // Gated on eligibility being settled so an eligible manager isn't
  // momentarily bumped to Rest Day before the OT tab resolves.
  useEffect(() => {
    const roOnlyKeys = RO_TABS.map((t) => t.key);
    if (
      isRO403 &&
      roOnlyKeys.includes(activeTab) &&
      !overtimeEligibilityQuery.isLoading
    ) {
      setActiveTab(overtimeEligible ? 'overtime' : 'restday');
    }
  }, [
    isRO403,
    activeTab,
    overtimeEligible,
    overtimeEligibilityQuery.isLoading,
    setActiveTab,
  ]);

  // If the user is sitting on Big Five without RO/HOD access (e.g. Trade
  // Leader with a stale ?tab=big-five URL), move them off it.
  useEffect(() => {
    if (activeTab === 'big-five' && !canViewBigFive && !overviewQuery.isLoading) {
      setActiveTab(
        isHOD
          ? 'department'
          : overtimeEligible
            ? 'overtime'
            : 'restday',
      );
    }
  }, [
    activeTab,
    canViewBigFive,
    overviewQuery.isLoading,
    isHOD,
    overtimeEligible,
    setActiveTab,
  ]);

  // If the user is sitting on the Overtime tab (e.g. persisted selection or
  // a shared ?tab=overtime URL) but turns out to have no overtime-eligible
  // workmen, move them off it once eligibility settles. ROs fall back to
  // their Overview; HOD-only users to the always-visible Rest Day tab.
  useEffect(() => {
    if (
      activeTab === 'overtime' &&
      !overtimeEligibilityQuery.isLoading &&
      !overtimeEligible
    ) {
      setActiveTab(isRO403 ? 'restday' : 'overview');
    }
  }, [
    activeTab,
    overtimeEligible,
    overtimeEligibilityQuery.isLoading,
    isRO403,
    setActiveTab,
  ]);

  const handleViewIndividual = (staffId) => {
    setSelectedStaffId(String(staffId));
    setActiveTab('individual');
  };

  return (
    <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">

        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <UsersRound className="w-4 h-4 sm:w-5 sm:h-5 text-blue-800" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">My Team</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
            {isHOD
              ? 'Attendance, wellbeing, Big Five career profiles, and department insights.'
              : 'Attendance, wellbeing, Big Five career profiles, and leave insights for your direct reports.'}
          </p>
        </div>

        {/* Not-an-RO (and not an HOD) empty state */}
        {showEmptyState && (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UsersRound className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-700 mb-2">No Direct Reports</h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              This dashboard is available to Reporting Officers and Heads of Department.
              You don't currently have any direct reports or department assignments.
            </p>
          </div>
        )}

        {!showEmptyState && (
          <>
            {/* Tabs — canonical tab-btn (employee theme) with scroll container */}
            <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
              {visibleTabs.map((tab) => {
                // RO-only tabs (overview/trends/leave/individual) are hidden
                // for HOD-only users; Big Five + Department + compliance stay.
                // Trade Leaders / Managers alone never see Big Five.
                if (tab.key === 'big-five' && !canViewBigFive) return null;
                if (!NON_RO_VISIBLE_KEYS.includes(tab.key) && isRO403) return null;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                    title={tab.label}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div className="min-h-[400px]">
              {activeTab === 'overview' && !isRO403 && (
                <TeamOverview
                  // Scope-aware data source. HODs in 'department' mode
                  // see the full department roster (which includes
                  // their indirect reports + cross-RO peers in the
                  // same dept). ROs / direct-reports mode always show
                  // the narrow ``reporting_officer = self`` set.
                  data={
                    isHOD && overviewScope === 'department'
                      ? hodOverviewQuery.data
                      : overviewQuery.data
                  }
                  isLoading={
                    isHOD && overviewScope === 'department'
                      ? hodOverviewQuery.isLoading
                      : overviewQuery.isLoading
                  }
                  error={
                    isHOD && overviewScope === 'department'
                      ? hodOverviewQuery.error
                      : overviewQuery.error
                  }
                  trendsData={trendsQuery.data}
                  leaveInsightsData={leaveQuery.data}
                  upcomingLeaveData={upcomingLeaveQuery.data}
                  onViewIndividual={handleViewIndividual}
                  // HOD scope toggle — TeamOverview renders the
                  // segmented control inline in the header when
                  // ``canToggleScope`` is true and hides it
                  // otherwise. State is owned here so other components
                  // (e.g. a future Trends scope filter) can reuse it
                  // without lifting the toggle UI out again.
                  canToggleScope={isHOD}
                  scope={overviewScope}
                  onScopeChange={setOverviewScope}
                />
              )}

              {activeTab === 'trends' && !isRO403 && (
                <TeamTrends
                  data={trendsQuery.data}
                  isLoading={trendsQuery.isLoading}
                  error={trendsQuery.error}
                />
              )}

              {activeTab === 'leave' && !isRO403 && (
                <LeaveInsights
                  data={leaveQuery.data}
                  isLoading={leaveQuery.isLoading}
                  error={leaveQuery.error}
                />
              )}

              {activeTab === 'individual' && (
                <IndividualDetail
                  data={individualQuery.data}
                  isLoading={individualQuery.isLoading}
                  error={individualQuery.error}
                  selectedStaffId={selectedStaffId}
                  teamMembers={teamMembers}
                  onSelectStaff={(id) => setSelectedStaffId(String(id))}
                  month={selectedMonth}
                  onMonthChange={setSelectedMonth}
                  isHOD={isHOD}
                />
              )}

              {activeTab === 'big-five' && canViewBigFive && <TeamBigFiveTab />}

              {activeTab === 'overtime' && overtimeEligible && <TeamOvertimePanel />}

              {activeTab === 'restday' && <TeamRestDayPanel />}

              {activeTab === 'department' && isHOD && (
                <DepartmentOverview
                  breakdownData={hodBreakdownQuery.data}
                  breakdownLoading={hodBreakdownQuery.isLoading}
                  breakdownError={hodBreakdownQuery.error}
                  overviewData={hodOverviewQuery.data}
                  overviewLoading={hodOverviewQuery.isLoading}
                  trendsData={hodTrendsQuery.data}
                  trendsLoading={hodTrendsQuery.isLoading}
                  leaveData={hodLeaveQuery.data}
                  leaveLoading={hodLeaveQuery.isLoading}
                  onViewIndividual={handleViewIndividual}
                />
              )}
            </div>
          </>
        )}
    </div>
  );
}
